import { SUPPORTED_COUNTRY_MAP } from './constants';
import { TAddress, TCountryCode } from './types';

export const ADDRESS_PREFIX_SEPARATOR = 'Sepa';

/**
 * Capitalises each word of a string the API returns in all caps (e.g. a suburb),
 * without disturbing a value that is already mixed case.
 */
const toTitleCase = (value: string) =>
  value
    .split(' ')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(' ');

/**
 * Composes an address as exactly two display lines from the structured fields,
 * rather than splitting `getAddressString`'s single formatted string:
 * - Line 1: unit number, street number, street name, street type.
 * - Line 2: suburb, state, postcode.
 *
 * The country is deliberately dropped (A14): `getAddressString` appends it as a
 * fourth line, which is the divergence this function exists to fix. Leave
 * `getAddressString` untouched, since nine other call sites depend on its
 * single-string, four-part shape.
 */
export const getAddressLines = (address: TAddress): string[] => {
  const street = [address.streetNumber, address.streetName, address.streetType]
    .filter(Boolean)
    .join(' ');

  const streetLine = address.unitNumber && street ? `${address.unitNumber}/${street}` : street;

  const localityLine = [
    address.suburb ? toTitleCase(address.suburb) : undefined,
    address.state,
    address.postcode,
  ]
    .filter(Boolean)
    .join(' ');

  return [streetLine, localityLine];
};

// Ideally, this method should cover all of address format,
// let's start with a simple one.
// TODO: to support all countries address format
export const getAddressString = (address: TAddress) => {
  if (!address) {
    return '';
  }

  if (Object.keys(address).length < 1) {
    return '';
  }

  const street = `${address.streetNumber ?? ''} ${address.streetName ?? ''} ${
    address.streetType ?? ''
  }`;
  return `${address.unitNumber ? address.unitNumber + '/' : ''} ${street ? street + ',' : ''} ${
    address.suburb ? address.suburb + ',' : ''
  } ${address.state ?? ''} ${address.postcode ?? ''}, ${
    SUPPORTED_COUNTRY_MAP[address.countryCode as TCountryCode]?.label
  }`;
};
